import { resolveElementFromPath } from '@src/utility/compositionSettings';
import { observer } from 'mobx-react-lite';

import { ImagePreset } from '../../ImagePreset';
import { PropertyCaption } from '../../PropertyCaption';

import type {
  ControlRenderProps,
  ImageControlSetting,
  LogoControlSetting,
} from '../ControlTypes';
import type { ElementState } from '@src/renderer/ElementState';

/**
 * ImageControl
 * Renders a lightweight preview (ImagePreset) and delegates selection to the existing global photo replacement modal.
 * On successful selection (modal closes with a new url), we call modifyProperty for each elementPath with the chosen url.
 * We listen to changes in the active element's source value to detect replacement rather than rewiring modal internals.
 */
export const ImageControl = observer(
  ({
    control,
    activeElement,
    modifyProperty,
    unifiedValue,
    isLogoControl = false,
  }: ControlRenderProps<ImageControlSetting | LogoControlSetting>) => {
    const initialUrl =
      typeof unifiedValue === 'string' ? unifiedValue : undefined;
    const imageElement = resolveElementFromPath(
      activeElement,
      control.elementPaths[0] || [],
    ) as unknown as ElementState;

    const onImageSourceChange = (newUrl: string) => {
      modifyProperty(control.elementPaths, control.property, newUrl);
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <PropertyCaption>{control.label}</PropertyCaption>
        <ImagePreset
          activeElement={imageElement}
          parentCompositionElement={activeElement}
          url={initialUrl || ''}
          onSourceChange={onImageSourceChange}
          isImageControl={!!isLogoControl}
          isLogoControl={isLogoControl}
        />
      </div>
    );
  },
);

export default ImageControl;
